import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaUsers, FaMotorcycle, FaClipboardList, FaRupeeSign, FaChartLine, FaCheckCircle, FaExclamationTriangle, FaClock, FaUserCheck } from 'react-icons/fa';
import SimpleChart from '../../components/SimpleChart';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
    fetchAnalytics();
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Fetch dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/api/admin/analytics', { params: { period } });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Fetch analytics error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Admin Dashboard</h1>
                <p className="text-sm sm:text-base text-gray-600">Welcome back, {user?.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Period:</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="input-field w-auto text-sm"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Total Users */}
            <div className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Users</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats?.stats?.totalUsers || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Active: {stats?.stats?.activeUsers || 0}</p>
                </div>
                <div className="bg-blue-100 p-2 sm:p-3 rounded-full">
                  <FaUsers className="text-blue-600 text-lg sm:text-xl" />
                </div>
              </div>
            </div>

            {/* Active Users */}
            <div className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Active Users</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{stats?.stats?.activeUsers || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.stats?.totalUsers > 0 
                      ? Math.round((stats.stats.activeUsers / stats.stats.totalUsers) * 100) 
                      : 0}% of total
                  </p>
                </div>
                <div className="bg-green-100 p-2 sm:p-3 rounded-full">
                  <FaUserCheck className="text-green-600 text-lg sm:text-xl" />
                </div>
              </div>
            </div>

            {/* Total Trips */}
            <div className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Trips</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats?.stats?.totalTrips || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Active: {stats?.stats?.activeTrips || 0}</p>
                </div>
                <div className="bg-purple-100 p-2 sm:p-3 rounded-full">
                  <FaMotorcycle className="text-purple-600 text-lg sm:text-xl" />
                </div>
              </div>
            </div>

            {/* Platform Revenue */}
            <div className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600">₹{stats?.stats?.totalRevenue?.toFixed(2) || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Today: ₹{stats?.stats?.todayRevenue?.toFixed(2) || 0}</p>
                </div>
                <div className="bg-yellow-100 p-2 sm:p-3 rounded-full">
                  <FaRupeeSign className="text-yellow-600 text-lg sm:text-xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="card bg-blue-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Riders</p>
                <p className="text-lg sm:text-xl font-bold text-blue-600">{stats?.stats?.totalRiders || 0}</p>
              </div>
            </div>
            <div className="card bg-green-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Passengers</p>
                <p className="text-lg sm:text-xl font-bold text-green-600">{stats?.stats?.totalPassengers || 0}</p>
              </div>
            </div>
            <div className="card bg-purple-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Bookings</p>
                <p className="text-lg sm:text-xl font-bold text-purple-600">{stats?.stats?.totalBookings || 0}</p>
              </div>
            </div>
            <div className="card bg-orange-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Completed</p>
                <p className="text-lg sm:text-xl font-bold text-orange-600">{stats?.stats?.completedTrips || 0}</p>
              </div>
            </div>
            <div className="card bg-yellow-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Pending Verify</p>
                <p className="text-lg sm:text-xl font-bold text-yellow-600">{stats?.stats?.pendingVerifications || 0}</p>
              </div>
            </div>
            <div className="card bg-indigo-50">
              <div className="text-center">
                <p className="text-xs text-gray-600 mb-1">Verified Riders</p>
                <p className="text-lg sm:text-xl font-bold text-indigo-600">{stats?.stats?.verifiedRiders || 0}</p>
              </div>
            </div>
          </div>

          {/* Charts Summary - Link to Analytics */}
          <div className="card mb-6 sm:mb-8 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 flex items-center">
                  <FaChartLine className="mr-2 text-blue-600" />
                  Analytics & Reports
                </h3>
                <p className="text-sm text-gray-600">
                  View detailed charts, graphs, and visualizations including bar charts, line charts, and pie charts
                </p>
              </div>
              <a href="/admin/analytics" className="btn-primary whitespace-nowrap">
                <FaChartLine className="inline mr-2" />
                View Full Analytics
              </a>
            </div>
          </div>

          {/* Detailed Stats & Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Today's Activity */}
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Today's Activity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">New Users</span>
                  <span className="font-semibold text-blue-600">{stats?.stats?.todayUsers || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">New Trips</span>
                  <span className="font-semibold text-green-600">{stats?.stats?.todayTrips || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">New Bookings</span>
                  <span className="font-semibold text-purple-600">{stats?.stats?.todayBookings || 0}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">Today's Revenue</span>
                  <span className="font-bold text-yellow-600">₹{stats?.stats?.todayRevenue?.toFixed(2) || 0}</span>
                </div>
              </div>
            </div>

            {/* User Breakdown */}
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">User Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center">
                    <FaMotorcycle className="mr-2 text-blue-500" />
                    Riders
                  </span>
                  <div className="text-right">
                    <span className="font-semibold">{stats?.stats?.totalRiders || 0}</span>
                    <p className="text-xs text-gray-500">
                      {stats?.stats?.verifiedRiders || 0} verified
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center">
                    <FaUsers className="mr-2 text-green-500" />
                    Passengers
                  </span>
                  <span className="font-semibold">{stats?.stats?.totalPassengers || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center">
                    <FaExclamationTriangle className="mr-2 text-yellow-500" />
                    Pending Verifications
                  </span>
                  <span className="font-semibold text-yellow-600">{stats?.stats?.pendingVerifications || 0}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Active Users</span>
                  <span className="font-bold text-green-600">{stats?.stats?.activeUsers || 0}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <a href="/admin/users" className="btn-primary w-full text-center block text-sm">
                  <FaUsers className="inline mr-2" />
                  Manage Users
                </a>
                <a href="/admin/trips" className="btn-secondary w-full text-center block text-sm">
                  <FaMotorcycle className="inline mr-2" />
                  View All Trips
                </a>
                <a href="/admin/bookings" className="btn-secondary w-full text-center block text-sm">
                  <FaClipboardList className="inline mr-2" />
                  View All Bookings
                </a>
                <a href="/admin/analytics" className="btn-secondary w-full text-center block text-sm">
                  <FaChartLine className="inline mr-2" />
                  View Analytics
                </a>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Users</h3>
              <div className="space-y-3">
                {stats?.recentUsers?.map((user) => (
                  <div key={user._id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <span className={`badge ${
                        user.role === 'rider' ? 'badge-info' : 'badge-success'
                      }`}>
                        {user.role}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-gray-500 text-center py-4">No recent users</p>
                )}
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Trips</h3>
              <div className="space-y-3">
                {stats?.recentTrips?.map((trip) => (
                  <div key={trip._id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{trip.rider?.name}</p>
                      <p className="text-sm text-gray-600">
                        {trip.origin?.address} → {trip.destination?.address}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`badge ${
                        trip.status === 'open' ? 'badge-info' :
                        trip.status === 'booked' ? 'badge-warning' :
                        trip.status === 'completed' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {trip.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        ₹{trip.price}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-gray-500 text-center py-4">No recent trips</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
